import { useEffect, useMemo, useState } from "react";

import { useExtendedSims } from "@/hooks/useExtendedSims.ts";
import { useGetSimQueryParamFilter } from "@/hooks/useGetSimQueryParamFilter.ts";
import { useItemizedBillPromoSession } from "@/hooks/useItemizedBillPromoSession.ts";
import { useNeedsIDCheck } from "@/hooks/useNeedsIDCheck.ts";
import {
  PORTAL_SIM_LIST_PAGE_SIZE,
  portalSimListInfiniteQueryOptions,
} from "@/hooks/usePortalSimListQuery.ts";
import { useGetEvnForCustomer, useGetSimListInfinite } from "@/services/api.ts";
import { hasQualifyingEvnAmount } from "@/utils/evnUtils.ts";
import {
  applyPrioritySortWithCap,
  PORTAL_SIM_PRIORITY_FETCH_CAP,
} from "@/utils/simDisplayPriority.ts";

export interface UsePortalDeviceTilesOptions {
  pageSize?: number;
  /** Client-side priority sort within the fetch cap (default: false). */
  sortByDisplayPriority?: boolean;
}

/**
 * Custom hook to manage the data and logic for the PortalDeviceTiles component.
 *
 * @param {boolean} showPromo - Whether to show the promo tile.
 * @param {boolean} showSims - Whether to show the SIM tiles.
 * @param {UsePortalDeviceTilesOptions} options - Page size and sort behaviour.
 * @returns {object} An object containing the SIM list, loading states, and promo visibility.
 */
export const usePortalDeviceTiles = (
  showPromo: boolean,
  showSims = true,
  options: UsePortalDeviceTilesOptions = {},
) => {
  const {
    sortByDisplayPriority = false,
    pageSize: pageSizeOption = PORTAL_SIM_LIST_PAGE_SIZE,
  } = options;
  const pageSize = sortByDisplayPriority
    ? PORTAL_SIM_PRIORITY_FETCH_CAP
    : pageSizeOption;

  const filter = useGetSimQueryParamFilter();
  const needsIdCheck = useNeedsIDCheck();
  const [refetchInterval, setRefetchInterval] = useState<false | number>(false);

  const { isPromoOpen: isPromoTimeWindowOpen } = useItemizedBillPromoSession({
    enabled: showPromo,
  });

  // Infinite query for fetching SIM cards
  const {
    data: simList,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    isError,
    hasNextPage,
  } = useGetSimListInfinite(
    {
      ...filter,
      pageSize,
    },
    {
      query: {
        enabled: showSims,
        ...portalSimListInfiniteQueryOptions,
        refetchInterval,
      },
    },
  );

  const flatSimListRaw = useMemo(
    () =>
      simList?.pages?.length
        ? simList.pages
            .map((page) => page?.simCards || [])
            .reduce((prevValue, currValue) => [...prevValue, ...currValue])
        : [],
    [simList],
  );

  const totalElements = simList?.pages?.[0]?.totalElements;

  // Enrich the list of Sims with roaming and esim data
  const {
    extendedSims: flatExtendedSimListRaw,
    hasUnscannedEsims,
    creditClass,
  } = useExtendedSims(flatSimListRaw, refetchInterval, needsIdCheck);

  const flatExtendedSimList = useMemo(
    () =>
      sortByDisplayPriority
        ? applyPrioritySortWithCap(
            flatExtendedSimListRaw,
            PORTAL_SIM_PRIORITY_FETCH_CAP,
          )
        : flatExtendedSimListRaw,
    [flatExtendedSimListRaw, sortByDisplayPriority],
  );

  const flatSimList = useMemo(() => {
    if (!sortByDisplayPriority) {
      return flatSimListRaw;
    }

    const byId = new Map(
      flatSimListRaw.map((sim) => [sim.simId, sim] as const),
    );

    return flatExtendedSimList
      .map((sim) => byId.get(sim.simId))
      .filter((sim): sim is NonNullable<typeof sim> => sim !== undefined);
  }, [flatExtendedSimList, flatSimListRaw, sortByDisplayPriority]);

  // Auto-refetch when unscanned eSIMs are present
  useEffect(() => {
    setRefetchInterval(hasUnscannedEsims && 10000);
  }, [hasUnscannedEsims]);

  // Fetch itemized bill overview for the promo card
  const { data: itemizedBillOverview } = useGetEvnForCustomer(undefined, {
    query: {
      refetchOnWindowFocus: false,
      enabled: isPromoTimeWindowOpen && showPromo,
    },
  });

  const shouldShowItemizedBillPromoCard = useMemo(
    () =>
      showPromo &&
      isPromoTimeWindowOpen &&
      hasQualifyingEvnAmount(itemizedBillOverview?.evns),
    [isPromoTimeWindowOpen, itemizedBillOverview?.evns, showPromo],
  );

  return {
    flatSimList,
    flatExtendedSimList,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    isError,
    hasNextPage,
    shouldShowItemizedBillPromoCard,
    pageSize,
    totalElements,
    creditClass,
  };
};
